import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Brain,
  Pencil,
  Trash2,
  Search,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiDelete, ApiError } from '../lib/apiClient';

const MyCorrections = ({ user, setUser }) => {
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editCode, setEditCode] = useState('');
  const [editComment, setEditComment] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    localStorage.getItem('decoded_sidebar_collapsed') === 'true'
  );

  useEffect(() => {
    const handleSidebarToggle = (e) => setSidebarCollapsed(e.detail.collapsed);
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  useEffect(() => { fetchCorrections(); }, []);

  const fetchCorrections = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/api/ai/corrections');
      setCorrections(data?.corrections || []);
      setTotal(data?.total || 0);
    } catch {
      toast.error('Failed to load corrections');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editItem || !editCode.trim()) return;
    try {
      await apiPut(`/api/ai/corrections/${editItem.feedback_id}`, {
        corrected_hs_code: editCode.trim(),
        comment: editComment.trim() || null,
      });
      toast.success('Correction updated');
      setEditOpen(false);
      fetchCorrections();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Update failed');
      } else {
        toast.error('Connection error');
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      await apiDelete(`/api/ai/corrections/${deleteItem.feedback_id}`);
      toast.success('Correction removed');
      setDeleteOpen(false);
      fetchCorrections();
    } catch {
      toast.error('Delete failed');
    }
  };

  const filtered = corrections.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.product_description || '').toLowerCase().includes(q)
      || (c.corrected_hs_code || '').includes(q)
      || (c.ai_suggested_code || '').includes(q);
  });

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Sidebar user={user} setUser={setUser} activePage="corrections" />

      <main className={`pt-20 lg:pt-20 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto">

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-outfit" data-testid="corrections-title">My Corrections</h1>
                <p className="text-slate-500 text-sm">Your HS code corrections that teach the AI to be more accurate</p>
              </div>
            </div>
          </div>

          {/* Stats + Search */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-2" data-testid="corrections-count">
                <span className="text-lg font-bold text-violet-700">{total}</span>
                <span className="text-sm text-violet-500 ml-1.5">{total === 1 ? 'correction' : 'corrections'}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-2">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                AI is learning from your expertise
              </div>
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by product or HS code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 text-sm"
                data-testid="corrections-search"
              />
            </div>
          </div>

          {/* Corrections List */}
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border shadow-sm p-12 text-center" data-testid="corrections-empty">
              <div className="w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-violet-300" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {search ? 'No matching corrections' : 'No corrections yet'}
              </h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                {search
                  ? 'Try a different search term.'
                  : 'When you correct an AI classification, it appears here. The AI uses your corrections to improve future results for similar products.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border shadow-sm overflow-hidden" data-testid="corrections-table">
              <div className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <div key={c.feedback_id} className="px-5 py-4 hover:bg-slate-50/50 transition-colors group">
                    <div className="flex items-start gap-4">
                      {/* Product description */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate" data-testid={`correction-desc-${c.feedback_id}`}>
                          {c.product_description || 'Product description not available'}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-md font-mono">
                            {c.ai_suggested_code || '—'}
                          </span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                          <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-md font-mono font-semibold">
                            {c.corrected_hs_code}
                          </span>
                          {c.comment && (
                            <span className="text-xs text-slate-400 truncate max-w-[200px]" title={c.comment}>
                              — {c.comment}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          }) : ''}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                          data-testid={`correction-edit-${c.feedback_id}`}
                          onClick={() => {
                            setEditItem(c);
                            setEditCode(c.corrected_hs_code);
                            setEditComment(c.comment || '');
                            setEditOpen(true);
                          }}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-50"
                          data-testid={`correction-delete-${c.feedback_id}`}
                          onClick={() => { setDeleteItem(c); setDeleteOpen(true); }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info Card */}
          <div className="mt-6 bg-violet-50 border border-violet-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-violet-800 text-sm">How AI Memory Works</p>
                <p className="text-xs text-violet-700 mt-1 leading-relaxed">
                  When you correct a classification, the AI remembers it. Next time you classify a similar product,
                  the AI uses your corrections as context to provide more accurate results. The more you correct, the smarter it gets for your specific product domain.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Correction</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Correct HS Code</label>
              <Input value={editCode} onChange={(e) => setEditCode(e.target.value)}
                className="mt-1 font-mono" placeholder="e.g. 8518.30" data-testid="edit-correction-code" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Note (optional)</label>
              <Input value={editComment} onChange={(e) => setEditComment(e.target.value)}
                className="mt-1" placeholder="Why this code is correct..." data-testid="edit-correction-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} data-testid="edit-correction-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Correction</DialogTitle>
            <DialogDescription>
              This will remove the correction from AI memory. The AI will no longer use this feedback for future classifications.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="delete-correction-confirm">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyCorrections;
